import { useCallback, useEffect, useRef, useState } from "react";
import { loadNativeGeolocation } from "@/lib/native-geolocation";
import { renewVenuePresence, type DevicePosition } from "@/lib/checkin/validateVenueCheckin";

const FOREGROUND_UPDATE_INTERVAL_MS = 2 * 60_000;
const MIN_UPDATE_GAP_MS = 30_000;
const POSITION_MAX_AGE_MS = 0;

interface Options {
  userId: string | undefined;
  enabled: boolean;
  isPaused: () => boolean;
  venueId: string | null;
  onCheckedOut: () => void;
}

/**
 * Modelo ancorado em check-in (v1):
 *  - A posição do dispositivo NUNCA é persistida. Ela existe apenas em memória
 *    e só é enviada ao servidor sob demanda (RPC `resolve_venue`), que devolve
 *    os estabelecimentos próximos sem guardar nada.
 *  - Sem localização em background: só primeiro plano, com o app aberto e
 *    apenas enquanto houver check-in ativo.
 *
 * Este hook mantém somente o estado de "quando a última posição foi lida",
 * usado pela UI para indicar se a localização está ativa ou desatualizada.
 */
export const useLocationTracker = ({ userId, enabled, isPaused, venueId, onCheckedOut }: Options) => {
  const [lastUpdateAt, setLastUpdateAt] = useState<number | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const trackingStartedRef = useRef(false);
  const nativeWatchIdRef = useRef<string | null>(null);
  const webWatchIdRef = useRef<number | null>(null);

  /**
   * Marca que temos uma posição válida em primeiro plano. Não grava nada:
   * lat/long ficam apenas na memória de quem chamou.
   */
  const persistPosition = useCallback(
    async (lat: number, lng: number, accuracy = 0) => {
      if (!userId || !venueId || isPaused()) return;
      if (!Number.isFinite(accuracy) || accuracy <= 0 || accuracy > 100) return;
      const now = Date.now();
      if (now - lastUpdateRef.current < MIN_UPDATE_GAP_MS) return;
      lastUpdateRef.current = now;
      const result = await renewVenuePresence(venueId, { latitude: lat, longitude: lng, accuracy });
      if (result.ok && result.status === "checked_out") {
        onCheckedOut();
        window.dispatchEvent(new Event("checkin:updated"));
        return;
      }
      if (result.ok && result.status === "renewed") setLastUpdateAt(now);
    },
    [userId, venueId, isPaused, onCheckedOut]
  );

  const requestOnce = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    // Só primeiro plano — nada é lido com o app em background.
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    navigator.geolocation.getCurrentPosition(
      (pos) => void persistPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
      (err) => {
        if (err.code !== err.PERMISSION_DENIED) {
          console.debug("[foreground-location] getCurrentPosition error", err.message);
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 15_000,
        maximumAge: POSITION_MAX_AGE_MS,
      }
    );
  }, [persistPosition]);

  useEffect(() => {
    if (!enabled || !userId) return;
    if (trackingStartedRef.current) return;
    trackingStartedRef.current = true;
    let cancelled = false;

    void requestOnce();
    intervalRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      void requestOnce();
    }, FOREGROUND_UPDATE_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") void requestOnce();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
    }

    void (async () => {
      const native = await loadNativeGeolocation();
      if (cancelled || !enabled || isPaused()) return;
      if (native) {
        const watchId = await native.watchPosition(
          { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
          (position) => {
            if (document.visibilityState !== "visible" || !position) return;
            void persistPosition(position.coords.latitude, position.coords.longitude, position.coords.accuracy);
          },
        );
        if (cancelled) {
          await native.clearWatch({ id: watchId });
          return;
        }
        nativeWatchIdRef.current = watchId;
      } else if (navigator.geolocation) {
        webWatchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            if (document.visibilityState !== "visible") return;
            void persistPosition(position.coords.latitude, position.coords.longitude, position.coords.accuracy);
          },
          () => undefined,
          { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
        );
      }
    })();

    return () => {
      cancelled = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (webWatchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(webWatchIdRef.current);
        webWatchIdRef.current = null;
      }
      const nativeWatchId = nativeWatchIdRef.current;
      nativeWatchIdRef.current = null;
      if (nativeWatchId) void loadNativeGeolocation().then((native) => native?.clearWatch({ id: nativeWatchId }));
      trackingStartedRef.current = false;
    };
  }, [enabled, userId, requestOnce, isPaused, persistPosition]);

  return { lastUpdateAt, persistPosition, requestOnce, BACKGROUND_UPDATE_INTERVAL_MS: FOREGROUND_UPDATE_INTERVAL_MS, POSITION_MAX_AGE_MS };
};
